import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const VendorViewsAnalytics = ({ vendorId }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!vendorId) return;

        const fetchAnalytics = async () => {
            setLoading(true);
            setError(null);

            try {
                // Call the database function to get analytics
                const { data, error: dbError } = await supabase
                    .rpc('get_vendor_view_analytics', { vendor_uuid: vendorId });

                if (dbError) {
                    throw dbError;
                }

                if (data && data.length > 0) {
                    setAnalytics(data[0]);
                } else {
                    // No data yet, set defaults
                    setAnalytics({
                        total_views: 0,
                        unique_viewers: 0,
                        views_today: 0,
                        views_this_week: 0,
                        views_this_month: 0
                    });
                }
            } catch (err) {
                console.error('Error fetching vendor analytics:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [vendorId]);

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-20 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="flex items-center space-x-2 text-error">
                    <Icon name="AlertCircle" size={20} />
                    <span>Failed to load analytics</span>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return null;
    }

    const stats = [
        {
            label: 'Total Views',
            value: analytics.total_views?.toLocaleString() || '0',
            icon: 'Eye',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
        },
        {
            label: 'Unique Viewers',
            value: analytics.unique_viewers?.toLocaleString() || '0',
            icon: 'Users',
            color: 'text-purple-600',
            bgColor: 'bg-purple-50'
        },
        {
            label: 'Today',
            value: analytics.views_today?.toLocaleString() || '0',
            icon: 'TrendingUp',
            color: 'text-green-600',
            bgColor: 'bg-green-50'
        },
        {
            label: 'This Week',
            value: analytics.views_this_week?.toLocaleString() || '0',
            icon: 'Calendar',
            color: 'text-orange-600',
            bgColor: 'bg-orange-50'
        },
        {
            label: 'This Month',
            value: analytics.views_this_month?.toLocaleString() || '0',
            icon: 'BarChart2',
            color: 'text-indigo-600',
            bgColor: 'bg-indigo-50'
        },
        {
            label: 'Avg. Per Day',
            value: analytics.views_this_month
                ? Math.round(analytics.views_this_month / 30).toLocaleString()
                : '0',
            icon: 'Activity',
            color: 'text-pink-600',
            bgColor: 'bg-pink-50'
        }
    ];

    return (
        <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="font-heading font-semibold text-xl text-text-primary">
                        Profile Views Analytics
                    </h3>
                    <p className="text-text-secondary text-sm mt-1">
                        Track how many users are viewing your profile
                    </p>
                </div>
                <Icon name="BarChart3" size={24} className="text-primary" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {stats.map((stat, index) => (
                    <div
                        key={index}
                        className={`${stat.bgColor} rounded-lg p-4 transition-all hover:shadow-medium`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <Icon name={stat.icon} size={20} className={stat.color} />
                        </div>
                        <div className={`text-2xl font-bold ${stat.color} mb-1`}>
                            {stat.value}
                        </div>
                        <div className="text-xs text-text-muted font-medium">
                            {stat.label}
                        </div>
                    </div>
                ))}
            </div>

            {analytics.total_views === 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-border-light">
                    <p className="text-text-secondary text-sm text-center">
                        <Icon name="Info" size={16} className="inline mr-1" />
                        Your profile hasn't been viewed yet. Share your profile link to get more visibility!
                    </p>
                </div>
            )}

            {analytics.total_views > 0 && (
                <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                    <div className="flex items-start space-x-3">
                        <Icon name="TrendingUp" size={20} className="text-primary mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-primary mb-1">Great visibility!</h4>
                            <p className="text-sm text-text-secondary">
                                {analytics.unique_viewers > 0 && (
                                    <>
                                        You've reached <strong>{analytics.unique_viewers}</strong> unique viewers.
                                        {analytics.unique_viewers > 10 && ' Keep up the great work! 🎉'}
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorViewsAnalytics;
